import { useState } from "react";
import { ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { openEstimate, openBudget } from "@/estimating/lib/navigation";
import HeaderFilterMenu from "@/estimating/components/HeaderFilterMenu";
import NotesThread from "@/components/NotesThread";
import axios from "axios";

import { AddCustomer } from "@/estimating/components/BoardCells";
import { preconApi } from "@/estimating/lib/api";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ContactCell } from "@/components/ContactCell";
import { GcContactCell } from "./GcContactCell";
import { EmailButton } from "./EmailButton";
import PersonChip from "@/estimating/components/PersonChip";

const API = `${window.location.origin}/api/budget`;

export const SPEC_STATUSES = [
  { value: "pending", label: "Pending", dot: "bg-slate-400" },
  { value: "basis_of_design", label: "Basis of Design", dot: "bg-emerald-500" },
  { value: "approved_equal", label: "Approved Equal", dot: "bg-teal-500" },
  { value: "not_listed", label: "Not Listed", dot: "bg-red-500" },
];
const DOT = Object.fromEntries(SPEC_STATUSES.map((s) => [s.value, s.dot]));
const LABEL = Object.fromEntries(SPEC_STATUSES.map((s) => [s.value, s.label]));
// Exactly one dot in the cell; clicking it opens the status menu.
const StatusSelect = ({ r, onChange }) => {
  const id = `${r.budget_id}-${r.scope_code}`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" title={LABEL[r.spec_status] || "Pending"} aria-label={LABEL[r.spec_status] || "Pending"}
                className="h-7 w-7 inline-flex items-center justify-center rounded-sm hover:bg-muted"
                data-testid={`spec-status-${id}`} data-status={r.spec_status}>
          <span className={`w-3 h-3 rounded-full ${DOT[r.spec_status] || DOT.pending}`} data-testid={`spec-status-dot-${id}`} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" data-testid={`spec-status-menu-${id}`}>
        {SPEC_STATUSES.map((s) => (
          <DropdownMenuItem key={s.value} onSelect={() => s.value !== r.spec_status && onChange(s.value)} className="text-xs gap-2"
                            data-testid={`spec-status-option-${s.value}`}>
            <span className={`w-2 h-2 rounded-full ${s.dot}`} />{s.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Notes — the shared NotesThread (UI_PATTERNS.md › Notes UI). */
const NotesCell = ({ r, onAdd }) => (
  <NotesThread notes={r.notes || []} title={r.project_name} testid={`spec-notes-${r.budget_id}-${r.scope_code}`}
               target={{ budget_id: r.budget_id, scope_code: r.scope_code }} onAdd={(text) => onAdd(r, text)} />
);

/** BOD rows (budget/bid × tracked scope), rendered by both Sales › Basis of Design and the Budgets dashboard. */
const TYPE_CHIP = { E: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200", B: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200" };
export const scopeLabel = (r) => r.scope_alias || r.scope_code || "";
const STATUS_ORDER = Object.fromEntries(SPEC_STATUSES.map((s, i) => [s.value, i]));
const openRow = (r) => (r.type !== "B"
  ? (r.estimate_id ? openEstimate(r.estimate_id) : window.dispatchEvent(new CustomEvent("abs:navigate", { detail: { tab: "bidboard" } })))
  : openBudget(r.budget_id));

const ProjectLink = ({ r }) => (
  <button type="button" onClick={() => openRow(r)} title={r.type !== "B" ? "Open the estimate workspace" : "Open the budget"}
          className="text-left text-foreground hover:underline underline-offset-2" data-testid={`spec-name-link-${r.budget_id}-${r.scope_code}`}>
    {r.project_name}
  </button>
);

// GC — names (comma list) + the Bid Board GC picker; the whole cell opens it when empty, "+" adds another once one exists.
const gcNames = (r) => (r.gc_contacts?.length ? r.gc_contacts.map((c) => c.gc)
  : (r.gc_name || r.customer || "").split(",").map((n) => n.trim()).filter(Boolean));
const GcCell = ({ r, id, onAdded }) => {
  const names = gcNames(r);
  const jobId = String(r.budget_id).startsWith("bid:") ? String(r.budget_id).slice(4) : "";
  const onPick = (name) => (jobId ? preconApi.addInvitation(jobId, { gc_name: name })
                                   : axios.patch(`${API}/admin/specs/${r.budget_id}`, { add_gc: name }));
  const picker = (trigger) => (
    <AddCustomer job={{ id: jobId, project_name: r.project_name }} already={names} onPick={onPick} onChanged={onAdded}
                 testid={`spec-gc-add-${id}`}>{trigger}</AddCustomer>
  );
  if (!names.length) {
    return picker(<button type="button" data-testid={`spec-gc-add-${id}`} title="Add GC"
                          className="block w-full min-w-[6rem] py-1 text-left text-muted-foreground hover:text-foreground">—</button>);
  }
  return (
    <span className="flex items-center gap-1.5">
      <span className="truncate" title={names.join("\n")}>{names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0]}</span>
      {picker()}
    </span>
  );
};

/** MANUF column — acronym per manufacturer, linked to its website when Budgets › Manufacturers has one. */
const ManufCell = ({ r }) => {
  const links = r.manufacturer_links || (r.manufacturer_codes || r.manufacturers || []).map((c) => ({ code: c, name: c, website: "" }));
  if (!links.length) return "—";
  return links.map((m, i) => (
    <span key={`${m.name}-${i}`}>{i > 0 && ", "}
      {m.website
        ? <a href={m.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title={`${m.name} — ${m.website}`}
             className="text-sky-700 hover:underline dark:text-sky-400" data-testid={`spec-manu-link-${r.budget_id}-${r.scope_code}-${i}`}>{m.code}</a>
        : <span title={m.name}>{m.code}</span>}
    </span>));
};

const fmtDate = (v) => new Date(`${v}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const dueTone = (v) => { if (!v) return "text-muted-foreground"; const d = Math.round((new Date(`${v}T00:00:00`) - new Date(new Date().toDateString())) / 86400000); return d <= 2 ? "text-red-600 font-semibold" : d <= 5 ? "text-amber-600 font-medium" : ""; };
const COLS = [
  { key: "name", label: "Name", get: (r) => r.project_name, cls: "px-3" },
  { key: "scope", label: "Scope", get: scopeLabel, filter: true, title: "Scope · manufacturer acronym (links to the website)" },
  { key: "architect", label: "Architect", get: (r) => r.architect },
  { key: "contact", label: "Contact", get: (r) => r.architect_contact_email || r.architect_contact_name },
  { key: "gc", label: "GC", get: (r) => r.gc_name || r.customer },
  { key: "gc_contact", label: "Contact", get: (r) => r.gc_contact_email || r.gc_contact_name },
  { key: "bid_due", label: "Bid Due", get: (r) => r.bid_due || "9999", title: "Internal bid due from the Bid Board / Budgets record this row came from" },
  { key: "am", label: "AM", get: (r) => r.account_manager },
  { key: "stage", label: "Stage", get: (r) => r.type || "B", title: "B = Budget (Budgets tab) · E = Estimate (Bid Board)" },
  { key: "status", label: "BOD", get: (r) => STATUS_ORDER[r.spec_status] ?? 0, title: "Basis of Design · Approved Equal · Not listed" },
  { key: "notes", label: "Notes", get: (r) => (r.notes || [])[0]?.at || "" },
  { key: "request", label: "Request", get: (r) => r.emails_sent || 0, cls: "text-right pr-2" },
];
const cmp = (a, b) => (typeof a === "number" && typeof b === "number" ? a - b : String(a || "").localeCompare(String(b || ""), undefined, { numeric: true, sensitivity: "base" }));

const SortHead = ({ c, sort, onSort, filter }) => {
  const on = sort.key === c.key;
  if (c.filter && filter) {
    return (
      <th className={`text-left pr-2 py-2 ${c.cls || ""}`} data-testid={`bod-col-${c.key}`}>
        <HeaderFilterMenu label={c.label} testid={`bod-${c.key}`} multi separator={filter.separator}
                          value={filter.value[c.key]} options={filter.options[c.key]} onChange={(v) => filter.onChange(c.key, v)}
                          onSort={(dir) => onSort(c.key, dir)} sortActive={on} direction={sort.dir} />
      </th>
    );
  }
  const Icon = !on ? ChevronsUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`text-left pr-2 py-2 ${c.cls || ""}`} title={c.title} aria-sort={on ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={() => onSort(c.key)} data-testid={`bod-sort-${c.key}`} data-sort={on ? sort.dir : ""}
              className={`inline-flex items-center gap-1 hover:text-foreground ${on ? "text-foreground" : ""}`}>
        {c.label}<Icon className={`w-3 h-3 ${on ? "" : "opacity-40"}`} />
      </button>
    </th>
  );
};

export const SpecTrackerTable = ({ rows, onPatch, onContact, onGcContacts, onEmail, onNote, onGcAdded, extraColumns = false, filter }) => {
  const [sort, setSort] = useState({ key: "", dir: "asc" });
  const cols = COLS;
  const onSort = (key, dir) => setSort((s) => (dir ? { key, dir } : s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const col = COLS.find((c) => c.key === sort.key);
  const sorted = col ? [...rows].sort((a, b) => cmp(col.get(a), col.get(b)) * (sort.dir === "asc" ? 1 : -1)) : rows;
  return (
    <TooltipProvider delayDuration={150}>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="bod-table">
          <thead className="bg-muted/40 text-[11px] font-semibold text-muted-foreground">
            <tr>{cols.map((c) => <SortHead key={c.key} c={c} sort={sort} onSort={onSort} filter={filter} />)}</tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const id = `${r.budget_id}-${r.scope_code}`;
              return (
                <tr key={id} className={`border-t border-border ${r.is_archived ? "opacity-60" : ""}`} data-testid={`spec-row-${id}`}>
                  <td className="px-3 py-1.5"><ProjectLink r={r} /></td>
                  <td className="whitespace-nowrap pr-2 py-1 leading-tight" title={`${r.scope_code} ${r.scope_name || ""}`} data-testid={`spec-scope-${id}`}>
                    <div className="font-mono font-semibold">{scopeLabel(r)}</div>
                    <div className="text-[10px] text-muted-foreground" title={(r.manufacturers || []).join(", ")} data-testid={`spec-manufacturer-${id}`}><ManufCell r={r} /></div>
                  </td>
                  <td className="pr-2" data-testid={`spec-architect-${id}`}>{r.architect || "—"}</td>
                  <td className="pr-2 py-1 whitespace-nowrap"><ContactCell name={r.architect_contact_name} email={r.architect_contact_email} testid={`spec-arch-contact-${id}`}
                                                                                   directory={{ companyName: r.architect || "", type: "architect" }}
                                                                                   onSave={(d) => onContact(r, { architect_contact_name: d.name, architect_contact_email: d.email })} /></td>
                  <td className="pr-2 max-w-[13rem]" data-testid={`spec-gc-${id}`}><GcCell r={r} id={id} onAdded={onGcAdded} /></td>
                  <td className="pr-2 py-1 whitespace-nowrap"><GcContactCell r={r} id={id} onSaveLegacy={onContact} onSaveGc={onGcContacts} /></td>
                  <td className={`pr-2 whitespace-nowrap ${dueTone(r.bid_due)}`} title={r.bid_due_gc ? `GC due ${fmtDate(r.bid_due_gc)}` : "No bid due — the row isn't linked to a Bid Board record yet"} data-testid={`spec-bid-due-${id}`}>{r.bid_due ? fmtDate(r.bid_due) : "—"}</td>
                  <td className="pr-2" data-testid={`spec-am-${id}`}>{r.account_manager ? <PersonChip name={r.account_manager} /> : "—"}</td>
                  <td className="pr-2"><span className={`rounded px-1 py-px text-[9px] font-semibold ${TYPE_CHIP[r.type] || "bg-muted"}`} title={r.type === "B" ? "Budget (Budgets tab)" : "Estimate (Bid Board)"} data-testid={`spec-type-${id}`}>{r.type || "B"}</span></td>
                  <td className="pr-1"><StatusSelect r={r} onChange={(v) => onPatch(r, { spec_status: v })} /></td>
                  <td className="pr-1"><NotesCell r={r} onAdd={onNote} /></td>
                  <td className="pr-2 text-right"><EmailButton r={r} onEmail={onEmail} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  );
};
